// REQ-1 · Vista pública de Seguimiento de Siniestros y Reclamos.
// El cliente ingresa el ID recibido por correo y ve el estado de su caso en dos líneas de hitos:
//   - línea principal: seguimiento del siniestro
//   - línea inferior:  seguimiento del vehículo en taller
// Tareas cubiertas: 10 (búsqueda), 11 (consumo API), 12 (resultado), 13 (líneas de estados), 14 (errores UX).

import { Button, Col, ConfigProvider, Divider, Input, Row, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Return } from '../../Components/Button/button';
import { HOME_ROUTE } from '../../Components/Router/constant';
import StatusTimeline from '../../Components/StatusTimeline/StatusTimeline';
import { useFormContext } from '../../Context/DeclarationContext';
import { formatDate } from '../../helpers/format-date';
import { ClaimStatusResponse } from '../../Interfaces/ClaimStatus.interface';
import { getClaimStatusById } from '../../Services/claimStatus';
import './claimStatus.scss';

const { Search } = Input;

const resolutionTag = (resolution?: string | null) => {
  switch (resolution) {
    case 'ACCEPTED':
      return <Tag color="green">Acogido</Tag>;
    case 'NOT_ACCEPTED':
      return <Tag color="red">No acogido</Tag>;
    case 'NULL':
      return <Tag>Nulo</Tag>;
    default:
      return null;
  }
};

// Sustantivo del caso según el tipo (capitalizado para títulos). El tipo lo determina el backend.
const caseNoun = (type?: string) => (type === 'THIRD_PARTY_CLAIM' ? 'Reclamo' : 'Siniestro');

// Normaliza lo que el usuario escribe/pega en el buscador. El ID admite dos formatos:
//   - Siniestro:            8 alfanuméricos            (ej. C92B09E8)
//   - Reclamo de terceros:  "RT-" + 6 alfanuméricos    (ej. RT-K7M2PQ)
// Por eso el guion debe conservarse: filtrarlo convertía "RT-K7M2PQ" en "RTK7M2PQ", que no
// existe en la BD y hacía fallar toda consulta de reclamos.
// Los guiones "largos" (– —) se normalizan a "-" porque el ID se pega desde el correo, donde
// Outlook/Word suelen autocorregir el guion simple.
const normalizeTrackingId = (raw: string) =>
  raw
    .toUpperCase()
    .replace(/[–—]/g, '-')
    .replace(/[^A-Z0-9-]/g, '');
// Título genérico mientras no se conoce el tipo (pantalla de búsqueda / al volver).
const GENERIC_TITLE = 'Seguimiento de siniestro o reclamo';

export const ClaimStatus = () => {
  const navigate = useNavigate();
  const { dispatch } = useFormContext();

  const [inputId, setInputId] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState<ClaimStatusResponse | null>(null);

  useEffect(() => {
    dispatch({
      type: 'SET_HEADER_TITLE',
      payload: GENERIC_TITLE,
    });
  }, []);

  const handleSearch = async (value: string) => {
    const id = (value || '').trim();
    setResult(null);
    setErrorMessage('');

    if (id.length === 0) {
      setErrorMessage('Por favor ingrese el ID de seguimiento que recibió por correo.');
      return;
    }
    if (id.length < 4) {
      setErrorMessage('El ID ingresado no es válido. Revíselo e intente nuevamente.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await getClaimStatusById(id);
      // El backend puede responder 200 con cuerpo vacío/parcial cuando el ID no existe.
      // Validar la forma mínima antes de mostrar el resultado, para no romper el render.
      if (!data || !Array.isArray(data.claimTimeline)) {
        setErrorMessage('No encontramos un caso con ese ID. Verifique el código de su correo e intente nuevamente.');
        return;
      }
      setResult(data);
      // Una vez conocido el caso, el título del header refleja el tipo real.
      dispatch({ type: 'SET_HEADER_TITLE', payload: `Seguimiento del ${caseNoun(data.type)}` });
      // El resultado es más alto que el buscador: sin esto, en móvil la vista queda
      // a media pantalla y el cliente no ve el encabezado del caso que consultó.
      window.scrollTo({ top: 0, left: 0 });
    } catch (error) {
      if (error?.status === 404 || error?.status === 500 || error?.status === 400) {
        setErrorMessage('No encontramos un caso con ese ID. Verifique el código de su correo e intente nuevamente.');
      } else if (error?.status === 504) {
        setErrorMessage('El servicio está tardando más de lo esperado. Intente nuevamente en unos minutos.');
      } else {
        setErrorMessage('En este momento no podemos procesar su solicitud. Intente más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setErrorMessage('');
    setInputId('');
    dispatch({ type: 'SET_HEADER_TITLE', payload: GENERIC_TITLE });
    window.scrollTo({ top: 0, left: 0 }); // dejar la vista en el tope al volver al buscador
  };

  const contactEmail = result?.contactEmail || 'siniestros@gamamobility.cl';

  return (
    <div className="claim-status" style={{ flexGrow: 1 }} aria-busy={loading}>
      <Row justify="center" className="claim-status__view">
        <Col xl={16} lg={20} md={22} sm={23} xs={23}>
          {/* --- Buscador por ID --- */}
          {!result && (
            <>
              <p className="claim-status__title">Seguimiento de tu siniestro o reclamo</p>
              <p className="claim-status__subtitle">
                Ingresa el ID de seguimiento que recibiste por correo al generar tu siniestro o reclamo.
              </p>

              {/* colorPrimary verde solo para este input: el hover/focus nativo de antd usa verde. */}
              <ConfigProvider theme={{ token: { colorPrimary: '#22b260' } }}>
                <Search
                  value={inputId}
                  onChange={(e) => setInputId(normalizeTrackingId(e.target.value))}
                  onSearch={handleSearch}
                  enterButton="Consultar"
                  placeholder="Ej: C92B09E8 o RT-K7M2PQ"
                  size="large"
                  className="claim-status__search"
                  loading={loading}
                />
              </ConfigProvider>

              {/* role="alert" para que el lector de pantalla anuncie el error: el foco
                  se queda en el input y sin esto el mensaje pasa inadvertido. */}
              {errorMessage && (
                <div className="claim-status__error" role="alert">
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Volver al portal público (/home), no al landing raíz. */}
              <Row style={{ marginTop: 24 }}>
                <Col>
                  <Return to={HOME_ROUTE} />
                </Col>
              </Row>
            </>
          )}

          {/* --- Vista de resultado --- */}
          {result && (
            <div className="claim-status__result">
              {/* Datos del cliente */}
              <p className="claim-status__section-title">Seguimiento de Cliente</p>
              {/* No se muestra `insured` (nombre del declarante), aunque el backend lo envía:
                  esta vista es pública y sin autenticar, basta el ID del correo para abrirla. */}
              <Row gutter={[24, 12]}>
                <Col xs={24} sm={12} md={10}>
                  <label className="claim-status__field-label">ID de seguimiento</label>
                  <Input className="claim-status__field" value={result.shortId} readOnly />
                </Col>
                <Col xs={24} sm={12} md={10}>
                  <label className="claim-status__field-label">Fecha de ingreso</label>
                  <Input className="claim-status__field" value={formatDate(result.createdAt)} readOnly />
                </Col>
              </Row>

              {/* Estado del siniestro */}
              <p className="claim-status__section-title" style={{ marginTop: 24 }}>
                Estado del {caseNoun(result.type)}
                {resolutionTag(result.resolution)}
              </p>
              <Row gutter={[24, 12]}>
                <Col xs={24} sm={12} md={10}>
                  <label className="claim-status__field-label">Patente</label>
                  <Input className="claim-status__field" value={result.plate} readOnly />
                </Col>
                <Col xs={24} sm={12} md={10}>
                  <label className="claim-status__field-label">Orden de Servicio</label>
                  <Input className="claim-status__field" value={result.serviceOrder || 'No aplica'} readOnly />
                </Col>
              </Row>

              {/* Línea principal: siniestro */}
              <div className="claim-status__timeline-block">
                <StatusTimeline
                  milestones={result.claimTimeline}
                  title={`Avance del ${caseNoun(result.type).toLowerCase()}`}
                />
              </div>

              {/* Línea inferior: vehículo en taller (solo siniestros; los reclamos no tienen OS) */}
              {(result.vehicleTimeline?.length ?? 0) > 0 && (
                <div className="claim-status__timeline-block">
                  <StatusTimeline milestones={result.vehicleTimeline} title="Vehículo en taller" />
                </div>
              )}

              <Divider />
              <p className="claim-status__contact">
                Si tiene dudas sobre el estado del {caseNoun(result.type).toLowerCase()}, comuníquese al correo de <strong>{contactEmail}</strong>
              </p>

              <Row gutter={[16, 12]} justify="center" className="claim-status__actions">
                <Col>
                  <Button className="claim-status__btn-secondary" onClick={handleReset}>
                    Volver atrás
                  </Button>
                </Col>
                <Col>
                  <Button className="claim-status__btn-primary" onClick={() => navigate('..')}>
                    Volver al sitio web
                  </Button>
                </Col>
              </Row>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ClaimStatus;
